import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/auth/AuthContext.js";  // ← fixed: one level up
import {
  Box, Typography, Button, Collapse, IconButton, Chip,
} from "@mui/material";
import {
  WarningAmberRounded,
  CancelOutlined,
  Close,
  CreditCard,
} from "@mui/icons-material";

const SubscriptionBanner = () => {
  const { state } = useContext(AuthContext);
  const { subscriptionStatus, subscriptionCode } = state;
  const role = state.role
    ?? (JSON.parse(localStorage.getItem("roles") || "[]")[0]);

  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  // Superadmin always has full access — never show banner
  if (role === "superadmin") return null;
  // Active subscription — nothing to show
  if (!subscriptionStatus || subscriptionStatus === "active") return null;

  const isPaused    = subscriptionStatus === "paused";
  const isCancelled = subscriptionStatus === "cancelled";
  if (!isPaused && !isCancelled) return null;

  const bgColor     = isPaused ? "#fff8e1" : "#fff3e0";
  const borderColor = isPaused ? "#f9a825" : "#e65100";
  const iconColor   = isPaused ? "#f57c00" : "#bf360c";
  const textColor   = isPaused ? "#6d4c00" : "#5c1a00";

  const Icon    = isPaused ? WarningAmberRounded : CancelOutlined;
  const label   = isPaused ? "Paused" : "Cancelled";
  const message = isPaused
    ? "Your subscription is paused. Some features may be limited."
    : "Your subscription has been cancelled. Please renew to continue using all features.";

  return (
    <Collapse in={open}>
      <Box
        sx={{
          width: "100%",
          bgcolor: bgColor,
          borderBottom: `2px solid ${borderColor}`,
          px: { xs: 2, sm: 3 },
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          zIndex: 1200,
        }}
      >
        {/* Left: icon + message */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
          <Icon sx={{ color: iconColor, fontSize: 22, flexShrink: 0 }} />
          <Chip
            label={label}
            size="small"
            sx={{
              bgcolor: borderColor,
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.65rem",
              height: 20,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: textColor, fontSize: "0.82rem", lineHeight: 1.4 }}
          >
            {message}
          </Typography>
        </Box>

        {/* Right: action button + close */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {role === "admin" && (
            <Button
              size="small"
              variant="contained"
              startIcon={<CreditCard sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/billing/manage-subscription")}
              sx={{
                bgcolor: borderColor,
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "none",
                borderRadius: "6px",
                px: 1.5,
                py: 0.5,
                "&:hover": { bgcolor: iconColor },
              }}
            >
              Manage subscription
            </Button>
          )}

          {role !== "admin" && (
            <Typography
              variant="caption"
              sx={{ color: textColor, fontStyle: "italic", fontSize: "0.75rem" }}
            >
              Contact your admin to renew.
            </Typography>
          )}

          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            sx={{ color: iconColor, p: 0.5 }}
            aria-label="dismiss banner"
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Collapse>
  );
};

export default SubscriptionBanner;